import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiDelete } from "~/shared/api/client";

import { userKeys } from "./profile-keys";

import type { AvatarCrop } from "./api";

// ---------------------------------------------------------------------------
// Account deletion types
// ---------------------------------------------------------------------------

export interface WorkspaceAdminInfo {
    id: string;
    email: string;
    name: string | null;
}

export interface OwnedWorkspacePreview {
    id: string;
    name: string;
    balance: number;
    membersCount: number;
    admins: WorkspaceAdminInfo[];
}

export interface DeletionPreviewResponse {
    ownedWorkspaces: OwnedWorkspacePreview[];
}

interface WorkspaceActionItem {
    workspaceId: string;
    action: "transfer" | "delete";
    toUserId?: string;
}

// ---------------------------------------------------------------------------
// Types (from original server/api/routers/user.ts)
// ---------------------------------------------------------------------------

export interface UserResponse {
    id: string;
    firstName: string | null;
    surname: string | null;
    name: string | null;
    email: string;
    image: string | null;
    imageOriginal: string | null;
    imageCrop: AvatarCrop | null;
    isSuperAdmin: boolean;
    status: string;
}

interface UpdateProfileImageResult {
    success: boolean;
    imgUrl: string | null;
    message: string;
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useUpdateProfile() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { firstName?: string; surname?: string; email?: string }) =>
            apiPatch<UserResponse>("/v2/users/me", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: userKeys.me });
        },
    });
}

export function useUpdateProfileImage() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            imageUrl: string;
            imageOriginalUrl?: string;
            imageCrop?: AvatarCrop | null;
        }): Promise<UpdateProfileImageResult> => {
            const result = await apiPatch<UserResponse>("/v2/users/me/image", input);
            return {
                success: true,
                imgUrl: result.image,
                message: "common:success.profile.updated",
            };
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: userKeys.me });
        },
    });
}

export function useDeletionPreview() {
    return useQuery({
        queryKey: [...userKeys.me, "deletion-preview"],
        queryFn: () => apiGet<DeletionPreviewResponse>("/v2/users/me/deletion-preview"),
        enabled: false, // manual trigger only
    });
}

export function useDeleteAccount() {
    return useMutation({
        mutationFn: async (input: { workspaceActions: WorkspaceActionItem[] }) => {
            await apiDelete<{ message: string }>("/v2/users/me", {
                workspaceActions: input.workspaceActions,
            });
            return { success: true };
        },
    });
}
